"use client"

import { useEffect, useState } from "react"
import { ArrowRight, Eye, EyeOff } from "lucide-react"
import { toast } from "sonner"
import { usePostAuthLoginProviderStart } from "openapi/api/endpoints"

import { CloudflareTurnstileGate } from "@/components/cloudflare-turnstile-gate"
import { isTurnstileRequired, TURNSTILE_LOGIN_ACTION } from "@/lib/turnstile"
import { cn } from "@/lib/utils"

/**
 * Sign in, from canvas artboards 13a (desktop dark), 13b (mobile dark),
 * 13c (desktop light) and 13d (mobile light).
 *
 * The canvas is explicit about what this screen is for: "the one screen every
 * user sees before they trust anything else, so it carries the claim the product
 * makes: the work runs whether or not you are watching. Left panel is proof, not
 * marketing — a ledger of what the system did overnight, in machine values,
 * unattended. Right panel is the form, and nothing else. No imagery, no
 * gradient, no marketing furniture."
 *
 * Two deliberate deviations from the artboard, both instructed:
 *
 *   - The artboard's two secondary buttons are Passkey and Single sign-on.
 *     Neither mechanism exists in the backend, and social auth is the only wired
 *     path, so the composition is kept exactly and the slots carry Google and
 *     Microsoft instead.
 *   - Email and password are presentation only. They render as drawn and accept
 *     typing, but there is no email/password endpoint, so Continue is inert and
 *     visibly disabled rather than a live-looking no-op. There is deliberately
 *     no <form onSubmit>, so Enter cannot post either.
 *
 * Unlike every other Graphite page this one responds to the *viewport*, not a
 * container: it hangs off rootRoute rather than AgentWorkspaceRoute, so it is
 * the one screen that renders outside the resizable ATS panel and the one place
 * min-h-screen is correct.
 */

/**
 * Artboard 13a's overnight ledger, verbatim. Machine values, so mono.
 *
 * This is an illustration of what Robin does while nobody is watching, and it
 * is labelled as one. Nobody is signed in on this screen, so there is no desk to
 * read and no live source this could come from — but under a bare "Last 24
 * hours" the fixed times, candidate counts and placement figures read as a claim
 * that this work actually happened overnight. The heading says "Example" now,
 * which costs nothing and stops the screen asserting something untrue.
 */
const LEDGER = [
  {
    time: "04:12",
    primitive: "longlist built",
    detail: "Staff Engineer @ Northwind · 38 candidates",
    short: "38 candidates",
    onMobile: true,
  },
  {
    time: "03:47",
    primitive: "replies triaged",
    detail: "12 of 14 answered",
    short: "12 of 14",
    onMobile: false,
  },
  {
    time: "01:20",
    primitive: "offer accepted",
    detail: "PLC-1042 · $32,000",
    short: "PLC-1042",
    onMobile: true,
  },
] as const

const EYEBROW = "font-mono font-medium tracking-[0.16em] text-brand-ink"
const FIELD_LABEL =
  "font-medium tracking-[0.14em] text-muted-foreground uppercase"
const FOOTPRINT =
  "font-mono tracking-[0.06em] text-muted-foreground"

/** The teal focus ring the artboard draws on the password field. */
const FIELD_FOCUS =
  "focus-within:border-primary focus-within:shadow-[0_0_0_3px_var(--auth-focus-glow)]"

const BUTTON_FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"

function useIsDesktop() {
  // The artboards split at 1440 and 390. Two columns need 560px of panel plus a
  // 372px form, so the switch belongs at lg, not at the app's 768px useIsMobile.
  const [isDesktop, setIsDesktop] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 1024px)").matches,
  )

  useEffect(() => {
    const mql = window.matchMedia("(min-width: 1024px)")
    const onChange = () => setIsDesktop(mql.matches)
    mql.addEventListener("change", onChange)
    return () => mql.removeEventListener("change", onChange)
  }, [])

  return isDesktop
}

export function LogoLockup() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex size-[26px] flex-none items-center justify-center rounded-md bg-primary font-bold text-[13px] leading-none text-primary-foreground">
        R
      </div>
      <span className="text-[13px] font-semibold leading-none">Recruiter OS</span>
    </div>
  )
}

/**
 * The proof panel. Desktop draws it as a hairline table directly on the chrome;
 * mobile draws it as a raised card pinned to the bottom, with the middle row
 * dropped.
 */
function OvernightLedger({ variant }: { variant: "table" | "card" }) {
  const rows = LEDGER.filter((row) => variant === "table" || row.onMobile)

  if (variant === "table") {
    return (
      <div className="flex flex-col">
        <div className="flex items-baseline justify-between pb-[11px]">
          <span className={cn(FIELD_LABEL, "text-[11px]")}>
            Example · a night of unattended work
          </span>
          <span className="font-mono text-[10.5px] uppercase text-muted-foreground">
            Unattended
          </span>
        </div>
        {rows.map((row, index) => (
          <div
            key={row.time}
            className={cn(
              "grid h-[46px] grid-cols-[56px_minmax(0,1fr)_auto] items-center gap-3.5 border-t border-border",
              index === rows.length - 1 && "border-b",
            )}
          >
            <span className="font-mono text-[11.5px] text-brand-ink">
              {row.time}
            </span>
            <span className="font-mono text-[11.5px]">{row.primitive}</span>
            <span className="text-[12px] text-muted-foreground">
              {row.detail}
            </span>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-[10px] border border-border bg-auth-raised">
      <div className="flex items-baseline justify-between px-[13px] pb-[9px] pt-[11px]">
        <span className={cn(FIELD_LABEL, "text-[10.5px]")}>Example · unattended</span>
        <span className="font-mono text-[10px] uppercase text-muted-foreground">
          Unattended
        </span>
      </div>
      {rows.map((row) => (
        <div
          key={row.time}
          className="flex items-center gap-[11px] border-t border-border px-[13px] py-2.5"
        >
          <span className="flex-none font-mono text-[11px] text-brand-ink">
            {row.time}
          </span>
          <span className="flex-none font-mono text-[11px]">{row.primitive}</span>
          <span className="ml-auto text-[11.5px] text-muted-foreground">
            {row.short}
          </span>
        </div>
      ))}
    </div>
  )
}

function GoogleMark() {
  return (
    <svg className="size-4 flex-none" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  )
}

function MicrosoftMark() {
  return (
    <svg className="size-4 flex-none" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#F25022" d="M11.4 11.4H0V0h11.4v11.4z" />
      <path fill="#00A4EF" d="M24 11.4H12.6V0H24v11.4z" />
      <path fill="#7FBA00" d="M11.4 24H0V12.6h11.4V24z" />
      <path fill="#FFB900" d="M24 24H12.6V12.6H24V24z" />
    </svg>
  )
}

type FieldProps = {
  id: string
  label: string
  type: "email" | "password" | "text"
  value: string
  onChange: (value: string) => void
  variant: "desktop" | "mobile"
  trailing?: React.ReactNode
  action?: React.ReactNode
  autoComplete: string
  placeholder?: string
  inputClassName?: string
}

function Field({
  id,
  label,
  type,
  value,
  onChange,
  variant,
  trailing,
  action,
  autoComplete,
  placeholder,
  inputClassName,
}: FieldProps) {
  const desktop = variant === "desktop"

  return (
    <div className="flex flex-col gap-[7px]">
      <div className="flex items-baseline justify-between">
        <label
          htmlFor={id}
          className={cn(FIELD_LABEL, desktop ? "text-[11px]" : "text-[10.5px]")}
        >
          {label}
        </label>
        {action}
      </div>
      <div
        className={cn(
          "flex items-center gap-2.5 border border-border px-3.5 transition-colors",
          FIELD_FOCUS,
          desktop
            ? "h-11 rounded-md bg-card text-[13.5px]"
            : "h-12 rounded-lg bg-auth-raised text-[14px] focus-within:bg-card",
        )}
      >
        <input
          id={id}
          name={id}
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          autoComplete={autoComplete}
          placeholder={placeholder}
          className={cn(
            "min-w-0 flex-1 bg-transparent text-inherit outline-none placeholder:text-muted-foreground/70",
            inputClassName,
          )}
        />
        {trailing}
      </div>
    </div>
  )
}

type SignInFormProps = {
  variant: "desktop" | "mobile"
  invite?: string
}

function SignInForm({ variant, invite }: SignInFormProps) {
  const desktop = variant === "desktop"

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [passwordVisible, setPasswordVisible] = useState(false)

  const [isLoading, setIsLoading] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [turnstileResetKey, setTurnstileResetKey] = useState(0)
  const { mutateAsync: startLogin } = usePostAuthLoginProviderStart()
  const turnstileRequired = isTurnstileRequired()

  const handleOAuthSignIn = async (provider: "google" | "azure") => {
    try {
      setIsLoading(true)
      const { authUrl } = await startLogin({
        provider,
        data: {
          turnstileToken: turnstileToken ?? undefined,
          ...(invite ? { invite } : {}),
        },
      })

      window.location.href = authUrl
    } catch {
      setIsLoading(false)
      setTurnstileResetKey((key) => key + 1)
      toast.error("Sign in verification failed", {
        description: "Please complete the security check and try again.",
        style: {
          background: "#dc2626",
          color: "white",
        },
      })
    }
  }

  const providersDisabled = isLoading || (turnstileRequired && !turnstileToken)

  const providerButton = (
    provider: "google" | "azure",
    mark: React.ReactNode,
    label: string,
  ) => (
    <button
      type="button"
      onClick={() => handleOAuthSignIn(provider)}
      disabled={providersDisabled}
      aria-label={`Continue with ${label}`}
      className={cn(
        "flex flex-1 items-center justify-center gap-2 border border-border text-[12.5px] font-medium transition-colors",
        "hover:bg-card disabled:cursor-not-allowed disabled:opacity-60",
        BUTTON_FOCUS,
        desktop ? "h-[42px] rounded-md" : "h-[46px] rounded-lg",
      )}
    >
      {mark}
      {desktop ? `Continue with ${label}` : label}
    </button>
  )

  return (
    <>
      <div className={cn("flex flex-col", desktop ? "gap-3.5" : "gap-3")}>
        <Field
          id={`${variant}-email`}
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          variant={variant}
          autoComplete="email"
          placeholder="you@company.com"
        />
        <Field
          id={`${variant}-password`}
          label="Password"
          type={passwordVisible ? "text" : "password"}
          value={password}
          onChange={setPassword}
          variant={variant}
          autoComplete="current-password"
          // 13a and 13b draw the masked value in mono at .18em — the dots are a
          // machine value like every other mono run on this screen. Only while
          // masked: revealed text is language and reads wrong spaced out.
          inputClassName={passwordVisible ? undefined : "font-mono tracking-[0.18em]"}
          action={
            // No reset flow exists, so this is a label, not a focusable no-op.
            <span className="text-[11.5px] text-brand-ink">Forgot?</span>
          }
          trailing={
            <button
              type="button"
              onClick={() => setPasswordVisible((visible) => !visible)}
              aria-label={passwordVisible ? "Hide password" : "Show password"}
              aria-pressed={passwordVisible}
              className={cn(
                "flex flex-none items-center text-muted-foreground transition-colors hover:text-foreground",
                BUTTON_FOCUS,
              )}
            >
              {passwordVisible ? (
                <EyeOff className={desktop ? "size-4" : "size-[18px]"} />
              ) : (
                <Eye className={desktop ? "size-4" : "size-[18px]"} />
              )}
            </button>
          }
        />
      </div>

      {/*
        Presentation only — there is no email/password endpoint. Visibly
        disabled rather than a live-looking button that swallows the click.
      */}
      <button
        type="button"
        disabled
        className={cn(
          "flex items-center justify-center gap-2 bg-primary font-medium text-primary-foreground opacity-60",
          "cursor-not-allowed",
          desktop
            ? "h-11 rounded-md text-[13.5px]"
            : "h-12 rounded-lg text-[14px]",
        )}
      >
        Continue
        <ArrowRight className="size-4" />
      </button>

      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-border" />
        <span className="text-[11px] text-muted-foreground">or</span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <CloudflareTurnstileGate
        action={TURNSTILE_LOGIN_ACTION}
        resetKey={turnstileResetKey}
        onTokenChange={setTurnstileToken}
      />

      <div className="flex gap-2.5">
        {providerButton("google", <GoogleMark />, "Google")}
        {providerButton("azure", <MicrosoftMark />, "Microsoft")}
      </div>
    </>
  )
}

export function GraphiteSignIn({ invite }: { invite?: string }) {
  const isDesktop = useIsDesktop()

  if (!isDesktop) {
    // Artboard 13b / 13d.
    return (
      <div className="graphite-auth flex min-h-screen flex-col bg-chrome px-6 pb-[30px] pt-[22px] text-foreground">
        <LogoLockup />

        <div className="mt-[34px] flex flex-col gap-3">
          <span className={cn(EYEBROW, "text-[10.5px] uppercase")}>
            Agentic ATS
          </span>
          <h1 className="text-pretty text-[27px] font-semibold leading-[1.2] tracking-[-0.03em]">
            The desk keeps working after you close it.
          </h1>
        </div>

        <div className="mt-[28px] flex flex-col gap-4">
          <SignInForm variant="mobile" invite={invite} />
        </div>

        <div className="min-h-[18px] flex-1" />

        <OvernightLedger variant="card" />

        <div className={cn(FOOTPRINT, "mt-4 text-center text-[10px] uppercase")}>
          SOC 2 Type II · EU data residency
        </div>
      </div>
    )
  }

  // Artboard 13a / 13c.
  return (
    <div className="graphite-auth flex h-screen overflow-hidden bg-background text-foreground">
      <div className="flex w-[560px] flex-none flex-col justify-between border-r border-border bg-chrome px-[46px] py-[38px]">
        <LogoLockup />

        <div className="flex max-w-[430px] flex-col gap-[18px]">
          <span className={cn(EYEBROW, "text-[11px] uppercase")}>
            Agentic ATS
          </span>
          <h1 className="text-pretty text-[38px] font-semibold leading-[1.16] tracking-[-0.03em]">
            The desk keeps working after you close it.
          </h1>
          <p className="text-pretty text-[14px] leading-[1.7] text-muted-foreground">
            Robin sources, screens and schedules against your live pipeline
            overnight. You sign in to the decisions, not the backlog.
          </p>
        </div>

        <OvernightLedger variant="table" />
      </div>

      <div className="relative flex flex-1 items-center justify-center">
        <div className="flex w-[372px] flex-col gap-[22px]">
          <div className="flex flex-col gap-[7px]">
            <div className="text-[26px] font-semibold leading-[1.15] tracking-[-0.025em]">
              Sign in
            </div>
            <div className="text-[13px] leading-[1.5] text-muted-foreground">
              Use your work email.
            </div>
          </div>

          <SignInForm variant="desktop" invite={invite} />

          <div className="text-[12px] leading-[1.6] text-muted-foreground">
            New here? Your admin sends the invite.
          </div>
        </div>

        <div
          className={cn(
            FOOTPRINT,
            "absolute inset-x-0 bottom-[30px] text-center text-[10.5px] uppercase",
          )}
        >
          SOC 2 Type II · EU data residency
        </div>
      </div>
    </div>
  )
}
